import { useTranslation } from 'react-i18next';

import {
  ACTION_VARIABLE_PRICE_TYPE,
  CAMPAIGN_VARIABLE_PRICE_TYPE,
} from '../../../config/constants';
import type {
  TInfSettingsAction,
  TInfSettingsRecurrentAction,
} from '../../../types/action';
import { SocialNetworkImage } from '../../images/SocialNetworkImage';
import { GenericDropdown } from '../../inputs/GenericDropdown';
import { MoneyInput } from '../../inputs/MoneyInput';
import { VariablePriceInput } from '../../inputs/VariablePriceInput';

type CampaignPriceSectionsProps = {
  control: any;
  influencer_variable_type: any;
  actions: TInfSettingsAction[] | TInfSettingsRecurrentAction[];
  watch: any;
  /**
   * Proposals derive the influencer's total price from the selected pack
   * (persisted server-side on pack selection) — it must not be manually
   * editable there. Campaign settings keep it editable (default).
   */
  totalAmountDisabled?: boolean;
  /**
   * Campaign agency fee billing model — locking state.
   *
   * - `agencyFeeEnabled`: when true AND `agencyFeePercentage != null`,
   *   `total_amount` is locked (computed server-side from
   *   `influencer_amount × (1 + agency_fee_percentage/100)`).
   * - `agencyFeeMode`: 'global' means the parent owns the fee %;
   *   the influencer-level fee field is locked. 'per_influencer'
   *   means the influencer can independently edit their fee %.
   * - `agencyFeePercentage`: current value of the per-influencer fee
   *   percentage (already loaded from the server). Drives lock
   *   decisions and the informational message below `total_amount`.
   */
  agencyFeeEnabled?: boolean;
  agencyFeeMode?: 'global' | 'per_influencer' | null;
  agencyFeePercentage?: number | null;
  /**
   * Bidirectional fee: the campaign-level direction.
   *
   *   - `'influencer'` (default): `influencer_amount` is the primary
   *     editable input; `total_amount` is server-derived.
   *   - `'brand'`: `total_amount` becomes the primary editable input
   *     and `influencer_amount` is server-derived.
   *
   * Only has effect when `agencyFeeEnabled === true`.
   */
  agencyFeeCalculationBase?: 'influencer' | 'brand' | null;
};

export const CampaignPriceSections = ({
  control,
  influencer_variable_type,
  actions,
  watch,
  totalAmountDisabled = false,
  agencyFeeEnabled = false,
  agencyFeeMode = null,
  agencyFeePercentage = null,
  agencyFeeCalculationBase = 'influencer',
}: CampaignPriceSectionsProps) => {
  const { t } = useTranslation('common');

  // Agency fee is active whenever the campaign-level flag is on,
  // regardless of whether a percentage has already been cascaded/set on
  // this specific row (a freshly-added influencer must still show Total
  // as locked — it's owned by the fee mechanism, not manually editable).
  const hasAgencyFee = agencyFeePercentage != null;
  // Bidirectional fee: in 'influencer' mode `total_amount` is derived,
  // in 'brand' mode `influencer_amount` is derived. The "primary"
  // editable field is the one that ISN'T derived. Proposers/packs
  // can still lock the total via `totalAmountDisabled`.
  const isBrandDirection =
    agencyFeeEnabled && agencyFeeCalculationBase === 'brand';
  const lockTotal =
    totalAmountDisabled || (agencyFeeEnabled && !isBrandDirection);
  const lockInfluencer = agencyFeeEnabled && isBrandDirection;
  // Per-influencer fee field is always parent-owned/locked while the
  // campaign is in global mode, whether or not a value has been
  // cascaded to this row yet.
  const lockAgencyFeeField = agencyFeeEnabled && agencyFeeMode === 'global';

  // Informational message below `total_amount` showing how the fee is
  // defined, with the exact monetary formula:
  // `fee = influencer_amount × agency_fee_percentage / 100`.
  // Shows whenever fee is enabled, even if the percentage/amount isn't
  // resolved yet (falls back to "0,00€" instead of hiding the message).
  const influencerAmount = watch('influencer_amount');
  const totalAmount = watch('total_amount');
  // Bidirectional fee: fee monetary is always the difference between
  // the two fields (total - influencer). Computing it from whichever
  // field is editable keeps the informational line accurate in both
  // directions.
  const feeMonetary = (() => {
    if (!hasAgencyFee) return 0;
    if (isBrandDirection && totalAmount != null && totalAmount !== '') {
      return (
        Math.round(
          Number(totalAmount) * (Number(agencyFeePercentage) / 100) * 100,
        ) / 100
      );
    }
    if (influencerAmount != null && influencerAmount !== '') {
      return (
        Math.round(
          Number(influencerAmount) * (Number(agencyFeePercentage) / 100) * 100,
        ) / 100
      );
    }
    return 0;
  })();
  const feeMessage = agencyFeeEnabled
    ? (() => {
        const formatted = feeMonetary.toLocaleString('es-ES', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        if (agencyFeeMode === 'global') {
          return `Definido en Ajustes Campaña · ${formatted}€`;
        }
        return `Editable por influencer · ${formatted}€`;
      })()
    : null;

  // eslint-disable-next-line no-nested-ternary
  const directionDisclaimer = agencyFeeEnabled
    ? isBrandDirection
      ? t('singleCampaign.agencyFee.disclaimerBrand')
      : t('singleCampaign.agencyFee.disclaimerInfluencer')
    : null;

  return (
    <>
      <div className="flex xl:flex-row flex-col w-full gap-5">
        <div className="w-full flex flex-row gap-x-5 items-center">
          <div>
            <MoneyInput
              name="total_amount"
              label={t('singleCampaign.totalPrice')}
              control={control}
              currentCurrency={watch('total_amountType')}
              tooltipText={
                totalAmountDisabled
                  ? t('singleCampaign.totalPriceFromPackTooltip')
                  : t('singleCampaign.totalPriceTooltip')
              }
              disabled={lockTotal}
            />
          </div>
          <span className="mt-8 -mx-3">+</span>
          <div className="mt-[37px]">
            <VariablePriceInput
              options={CAMPAIGN_VARIABLE_PRICE_TYPE}
              currencySelect={true}
              name="total_variable_amount"
              currentCurrency={
                watch(`influencer_variable_type`) === 'Revenue Share'
                  ? '%'
                  : watch('total_amountType')
              }
              registerName="influencer_variable_type"
              control={control}
              dropdownDisabled
              amountInputDisabled={
                watch('influencer_variable_type') === 'No' ||
                watch('influencer_variable_type') === 'Product' ||
                watch('influencer_variable_type') === 'Per Action'
              }
            />
          </div>
        </div>
        {agencyFeeEnabled && (
          <div className="w-full xl:w-[140px] xl:flex-none flex flex-row items-center">
            <div className="relative w-full">
              <MoneyInput
                name="agencyFeePercentage"
                label="Fee agencia"
                control={control}
                currentCurrency="%"
                currencySelect={false}
                disabled={lockAgencyFeeField}
              />
              {(watch('agencyFeePercentage') == null ||
                watch('agencyFeePercentage') === '') && (
                <span className="pointer-events-none absolute right-3 top-[44px] h-[41px] flex items-center text-sm font-medium text-[#5A5A5A]">
                  %
                </span>
              )}
            </div>
          </div>
        )}
        <div className="w-full flex flex-row gap-x-5 items-center">
          <div>
            <MoneyInput
              currencySelect={true}
              name="influencer_amount"
              currentCurrency={watch('influencer_amountType')}
              registerName="influencer_amountType"
              label={t('singleCampaign.influencerPrice')}
              control={control}
              disabled={lockInfluencer}
            />
          </div>
          <span className="mt-8 -mx-3">+</span>
          <div className="mt-[37px]">
            <VariablePriceInput
              options={CAMPAIGN_VARIABLE_PRICE_TYPE}
              currencySelect={true}
              name="influencer_variable_amount"
              currentCurrency={
                watch(`influencer_variable_type`) === 'Revenue Share'
                  ? '%'
                  : watch(`influencer_amountType`)
              }
              registerName="influencer_variable_type"
              control={control}
              amountInputDisabled={
                watch('influencer_variable_type') === 'No' ||
                watch('influencer_variable_type') === 'Product' ||
                watch('influencer_variable_type') === 'Per Action'
              }
            />
          </div>
        </div>
      </div>
      {feeMessage && <p className="text-sm text-gray-500 mt-1">{feeMessage}</p>}
      {directionDisclaimer && (
        <p className="text-sm text-gray-500 mt-1">{directionDisclaimer}</p>
      )}
      {influencer_variable_type === 'Per Action' && (
        <div className="flex flex-col mt-5 gap-5">
          <span>{t('singleCampaign.pricePerAction')}</span>
          {actions.map((a, i) => (
            <div
              key={`importe-${a.id}`}
              className="flex flex-row w-full justify-between gap-x-5"
            >
              <div className="flex flex-row text-sm items-center gap-x-2 font-medium">
                <SocialNetworkImage
                  socialNetwork={a.platform}
                  size="size-[30px]"
                />
                {a.number_of_action && a.type ? (
                  <span>
                    {a.platform} - {a.number_of_action}{' '}
                    {t(`actionType.${a.type}`)}
                  </span>
                ) : (
                  <span>{a.platform}</span>
                )}
              </div>
              <div className="flex flex-row gap-x-5">
                <div className="min-w-[250px]">
                  <GenericDropdown
                    options={ACTION_VARIABLE_PRICE_TYPE.map((val) => ({
                      label: t(`variable_price_type.${val}`),
                      value: val,
                    }))}
                    isClearable={false}
                    control={control}
                    validation={{}}
                    name={`actions.${i}.variable_type`}
                  />
                </div>
                <div className="flex flex-row gap-x-2 items-center">
                  <p className="text-sm text-nowrap">
                    {t('singleCampaign.totalPrice')}
                  </p>
                  <MoneyInput
                    currencySelect={
                      watch(`actions.${i}.variable_type`) !== 'Revenue Share'
                    }
                    name={`actions.${i}.total_amount`}
                    currentCurrency={
                      watch(`actions.${i}.variable_type`) === 'Revenue Share'
                        ? '%'
                        : watch(`actions.${i}.total_currency`)
                    }
                    registerName={`actions.${i}.total_currency`}
                    control={control}
                  />
                </div>
                <div className="flex flex-row gap-x-2 items-center">
                  <p className="text-sm text-nowrap">
                    {t('singleCampaign.influencerPrice')}
                  </p>
                  <MoneyInput
                    currencySelect={
                      watch(`actions.${i}.variable_type`) !== 'Revenue Share'
                    }
                    name={`actions.${i}.influencer_amount`}
                    currentCurrency={
                      watch(`actions.${i}.variable_type`) === 'Revenue Share'
                        ? '%'
                        : watch(`actions.${i}.total_currency`)
                    }
                    registerName={`actions.${i}.influencer_currency`}
                    control={control}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};
